import React, { useState } from 'react';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

type ToggleButtonSize = 'sm' | 'md' | 'lg';
type ToggleButtonVariant = 'secondary' | 'tertiary';

export interface ToggleButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /**
   * Estado activo controlado externamente.
   * Si se omite, el componente maneja su propio estado.
   */
  active?: boolean;
  /**
   * Estado activo inicial (modo no controlado)
   */
  defaultActive?: boolean;
  /**
   * Callback al cambiar el estado
   */
  onChange?: (active: boolean) => void;
  size?: ToggleButtonSize;
  variant?: ToggleButtonVariant;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

// ─────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────

const baseStyles = `
  inline-flex items-center justify-center
  font-['Inter'] font-medium
  rounded-lg border transition-all duration-200
  cursor-pointer whitespace-nowrap
  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand
  disabled:opacity-50 disabled:cursor-not-allowed
`;

const sizeStyles: Record<ToggleButtonSize, string> = {
  sm: 'h-8 px-4 text-ui-sm gap-2',
  md: 'h-10 px-5 text-ui-base gap-2',
  lg: 'h-12 px-7 text-body gap-2',
};

const iconOnlySizeStyles: Record<ToggleButtonSize, string> = {
  sm: 'w-8 h-8 p-2',
  md: 'w-10 h-10 p-2',
  lg: 'w-12 h-12 p-2',
};

const iconSizes: Record<ToggleButtonSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-[18px] h-[18px]',
  lg: 'w-5 h-5',
};

const variantStyles: Record<ToggleButtonVariant, { inactive: string; active: string }> = {
  secondary: {
    inactive: 'bg-surface-0 text-secondary border-default hover:bg-surface-1 hover:border-focus',
    active:   'bg-brand text-inverse border-transparent hover:bg-brand-hover',
  },
  tertiary: {
    inactive: 'bg-transparent text-tertiary border-transparent hover:bg-surface-1',
    active:   'bg-brand-light text-brand border-transparent',
  },
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      active: activeProp,
      defaultActive = false,
      onChange,
      size = 'md',
      variant = 'secondary',
      icon,
      iconPosition = 'left',
      iconOnly = false,
      disabled = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isControlled = activeProp !== undefined;
    const [internalActive, setInternalActive] = useState(defaultActive);
    const active = isControlled ? activeProp : internalActive;

    const handleClick = () => {
      const next = !active;
      if (!isControlled) setInternalActive(next);
      onChange?.(next);
    };

    const classes = [
      baseStyles,
      iconOnly ? iconOnlySizeStyles[size] : sizeStyles[size],
      active ? variantStyles[variant].active : variantStyles[variant].inactive,
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ');

    const renderIcon = (iconElement: React.ReactNode) => {
      if (!iconElement) return null;
      return (
        <span className={`inline-flex items-center flex-shrink-0 ${iconSizes[size]}`}>
          {iconElement}
        </span>
      );
    };

    const buttonContent = iconOnly && icon ? icon : (
      <>
        {icon && iconPosition === 'left' && renderIcon(icon)}
        {!iconOnly && <span className="inline-flex items-center leading-[1]">{children}</span>}
        {icon && iconPosition === 'right' && renderIcon(icon)}
      </>
    );

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-pressed={active}
        className={classes}
        onClick={handleClick}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';


